// Copyright (c) StrangeBee 2023

import React, { ReactElement, useMemo } from 'react';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import { THLayout } from '@Molecules/THLayout';
import { AccountInfosForm } from '@Organisms/AccountDetails/AccountInfos/AccountInfosForm';
import { useCurrentUser } from '@Queries/userQueries';
import { computeEventList } from '@Organisms/User/eventList';
import { useLocalMessage } from '@Helpers/locale';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import client from '@Client';
import useValidateForm from '@Hooks/useValidateForm';

import './Infos.scss';

const Infos = (): ReactElement => {
    const { data: currentUser, refetch } = useCurrentUser();
    const i = useLocalMessage();
    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();
    const { _id: userId = '' } = currentUser || {};

    const userEventList = useMemo(
        () =>
            computeEventList({
                locale: i,
                controller: client.UserController,
                onSuccess: (_, message) => {
                    if (message) {
                        openMessage('success', message);
                    }
                    refetch().then(() => {
                        setFieldsTouched(false);
                        form.resetFields();
                    });
                },
                onError: handleNotificationError,
            }),
        [i, refetch, form, setFieldsTouched],
    );

    return (
        <THLayout>
            <THLayout.Content>
                <AccountInfosForm
                    fieldsTouched={fieldsTouched}
                    user={currentUser}
                    form={form}
                    disabled={!isValid}
                    setFieldsTouched={setFieldsTouched}
                    onChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    onFinish={(user) => {
                        userEventList.onEdit(user, userId);
                    }}
                />
            </THLayout.Content>
        </THLayout>
    );
};

export const Route: RouteType = {
    menu: {
        label: 'Infos',
    },
    path: RouteBuilder.compute('account.infos'),
    content: <Infos />,
    space: ['admin', 'user'],
    breadcrumb: { label: 'Infos' },
};
